import NextLink from 'next/link';
import NextImage from 'next/image';
import {useRouter} from 'next/router';
import {Flex, Text, LinkOverlay, LinkBox, Image, Icon, IconProps, LinkBoxProps, Center} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {useAppSettingsQuery} from '@eksab/hooks';
import {getLanguage} from '@eksab/util';

import background from './background.png';
import android from './android.png';
import ios from './ios.png';
import {useShouldPromoteMobileApp} from '@eksab/hooks';

export const DownloadAppBanner = (props: LinkBoxProps) => {
  const t = useTranslate();
  const appSettingQuery = useAppSettingsQuery();

  const hasAPK = !!appSettingQuery.data?.apk_link;
  const isOnAppStore = !!appSettingQuery.data?.app_store_link;

  const shouldShowDownloadAppBanner = useShouldPromoteMobileApp();

  if (!shouldShowDownloadAppBanner) return null;

  return (
    <LinkBox
      as="article"
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap="4"
      isolation="isolate"
      ps={[4, 5, 6, 7]}
      pe={[2, 3, null, 4]}
      py={[1, 2, 3, 3.5]}
      rounded="1.25rem"
      w="45.5rem"
      maxW="full"
      minH="14"
      sx={{aspectRatio: '6.6'}}
      {...props}
    >
      <NextImage src={background} alt="" fill style={{objectFit: 'cover', zIndex: -1, borderRadius: 'inherit'}} />

      <Flex align="flex-start" flexDir="column" gap={[0.5, 1, 2]}>
        <Text fontWeight="600" fontSize={['xs', 'md', '2xl', '1.75rem']} color="white">
          {t('download-banner.title', {
            b: (chunks) => (
              <Text as="strong" color="inherit" fontSize="inherit" fontWeight="900">
                {chunks}
              </Text>
            ),
          })}
        </Text>

        <Flex gap={[0.5, 1, 2]} align="center">
          {hasAPK && <Vendor name="android" />}

          {isOnAppStore && <Vendor name="ios" />}
        </Flex>
      </Flex>

      <Flex align="flex-end" flexDir="column" gap={[0.5, 1, 1.5]}>
        <Flex
          py={[0.5, null, 1]}
          px={[1, 1.5, 2]}
          gap={[1, 1.5, 2]}
          align="center"
          rounded="full"
          borderWidth={['1px', null, '2px']}
          borderColor="#023F22"
          bg="#406454"
        >
          <SecureIcon boxSize={[3, 4, 5]} />

          <Divider />

          <Text fontSize={['0.5rem', 'xx-small', 'x-small', 'xs']} fontWeight="700" color="white">
            {t('download-banner.safe-and-secure')}
          </Text>
        </Flex>

        <LinkOverlay
          as={NextLink}
          href="/download"
          display="flex"
          alignItems="center"
          gap={[0.5, null, 1]}
          ps={[2, 3, 4, 5]}
          pe={[1, 1.5, 2, 2]}
          py={[0.5, null, null, 2]}
          rounded="full"
          fontSize={['0.5rem', 'xs', 'md', 'lg']}
          fontWeight={[700, null, 800]}
          bg="green.400"
          color="white"
          onClick={() => mixpanel?.track('download_app_banner')}
        >
          {t('download-banner.cta')}

          <ArrowIcon boxSize={[4, 5, 6]} />
        </LinkOverlay>
      </Flex>
    </LinkBox>
  );
};

const Vendor = ({name}: {name: 'android' | 'ios'}) => {
  const isAndroid = name === 'android';

  const t = useTranslate();

  return (
    <Center
      px={[0.5, 1, 1.5]}
      py={[1, null, 1.5]}
      boxSize={['1.125rem', 5, 6, 8]}
      rounded="full"
      bg={isAndroid ? '#F3FFD8' : 'black'}
    >
      <Image
        src={isAndroid ? android.src : ios.src}
        alt={t(`download.vendors.${name}`)}
        boxSize="full"
        objectFit="contain"
      />
    </Center>
  );
};

const Divider = (props: IconProps) => (
  <Icon width="2px" height="10px" viewBox="0 0 2 10" fill="none" {...props}>
    <path d="M1 0.75V9.25" stroke="white" strokeWidth="0.5" strokeLinecap="round" />
  </Icon>
);

const SecureIcon = (props: IconProps) => (
  <Icon width="13px" height="13px" viewBox="0 0 13 13" fill="none" {...props}>
    <path
      d="M9.29395 2.31561L6.54395 1.28561C6.25895 1.18061 5.79395 1.18061 5.50895 1.28561L2.75895 2.31561C2.22895 2.51561 1.79895 3.13561 1.79895 3.70061V7.75061C1.79895 8.15561 2.06395 8.69062 2.38895 8.93062L5.13895 10.9856C5.62395 11.3506 6.41895 11.3506 6.90395 10.9856L9.65395 8.93062C9.97895 8.68562 10.244 8.15561 10.244 7.75061V3.70061C10.249 3.13561 9.81895 2.51561 9.29395 2.31561ZM7.76395 5.11561L5.61395 7.26561C5.53895 7.34061 5.44395 7.37561 5.34895 7.37561C5.25395 7.37561 5.15895 7.34061 5.08395 7.26561L4.28395 6.45561C4.13895 6.31061 4.13895 6.07061 4.28395 5.92561C4.42895 5.78061 4.66895 5.78061 4.81395 5.92561L5.35395 6.46561L7.23895 4.58061C7.38395 4.43561 7.62395 4.43561 7.76895 4.58061C7.91395 4.72561 7.91395 4.97061 7.76395 5.11561Z"
      fill="white"
    />
  </Icon>
);

const ArrowIcon = (props: IconProps) => {
  const {locale} = useRouter();

  return (
    <Icon
      width="24px"
      height="25px"
      viewBox="0 0 24 25"
      fill="none"
      transform="auto"
      scaleX={getLanguage(locale) === 'ar' ? -1 : 1}
      color="white"
      {...props}
    >
      <path
        d="M13.7071 16.2071C13.3166 16.5976 12.6834 16.5976 12.2929 16.2071C11.9024 15.8166 11.9024 15.1834 12.2929 14.7929L13.5858 13.5H8C7.44771 13.5 7 13.0523 7 12.5C7 11.9477 7.44771 11.5 8 11.5H13.5858L12.2929 10.2071C11.9024 9.81658 11.9024 9.18342 12.2929 8.79289C12.6834 8.40237 13.3166 8.40237 13.7071 8.79289L16.7071 11.7929C16.8946 11.9804 17 12.2348 17 12.5C17 12.7652 16.8946 13.0196 16.7071 13.2071L13.7071 16.2071Z"
        fill="currentColor"
      />
    </Icon>
  );
};
